<template>
  <div class="modal fade" id="payGradesManagementModal" tabindex="-1" aria-labelledby="payGradesManagementModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="payGradesManagementModalLabel">Pay Grades Management</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary me-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading pay grades...</p>
          </div>

          <div v-else>
            <!-- Controls section -->
            <div class="d-flex justify-content-between mb-3">
              <h6 class="mb-0 mt-2">Manage Pay Grades</h6>
              <button @click="showAddModal" class="btn btn-sm btn-primary">
                <i class="mdi mdi-plus me-1"></i> Add Pay Grade
              </button>
            </div>

            <!-- Pay grades table -->
            <div class="table-responsive">
              <table class="table table-sm table-hover table-bordered">
                <thead class="table-light">
                <tr>
                  <th>Grade Name</th>
                  <th>Min Salary</th>
                  <th>Max Salary</th>
                  <th>Midpoint</th>
                  <th>Description</th>
                  <th class="text-center">Actions</th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="payGrades.length === 0">
                  <td colspan="6" class="text-center py-3">No pay grades found</td>
                </tr>
                <tr v-for="grade in payGrades" :key="grade.id">
                  <td>{{ grade.grade_name }}</td>
                  <td>{{ formatCurrency(grade.min_salary) }}</td>
                  <td>{{ formatCurrency(grade.max_salary) }}</td>
                  <td>{{ formatCurrency(grade.midpoint_salary) }}</td>
                  <td>{{ grade.description || 'N/A' }}</td>
                  <td class="text-center">
                    <button @click="editPayGrade(grade)" class="btn btn-sm btn-info me-1">
                      <i class="mdi mdi-pencil"></i>
                    </button>
                    <button @click="deletePayGrade(grade.id)" class="btn btn-sm btn-danger">
                      <i class="mdi mdi-trash-can"></i>
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add/Edit Pay Grade Modal -->
  <div class="modal fade" id="payGradeFormModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEditing ? 'Edit Pay Grade' : 'Add Pay Grade' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="savePayGrade">
            <div class="mb-3">
              <label for="gradeName" class="form-label">Grade Name <span class="text-danger">*</span></label>
              <input
                  type="text"
                  class="form-control"
                  id="gradeName"
                  v-model="gradeForm.grade_name"
                  required
              >
            </div>

            <div class="mb-3">
              <label for="minSalary" class="form-label">Minimum Salary <span class="text-danger">*</span></label>
              <div class="input-group">
                <span class="input-group-text">KES</span>
                <input
                    type="number"
                    class="form-control"
                    id="minSalary"
                    v-model="gradeForm.min_salary"
                    required
                    min="0"
                    step="0.01"
                >
              </div>
            </div>

            <div class="mb-3">
              <label for="maxSalary" class="form-label">Maximum Salary <span class="text-danger">*</span></label>
              <div class="input-group">
                <span class="input-group-text">KES</span>
                <input
                    type="number"
                    class="form-control"
                    id="maxSalary"
                    v-model="gradeForm.max_salary"
                    required
                    min="0"
                    step="0.01"
                >
              </div>
              <small v-if="salaryError" class="text-danger">{{ salaryError }}</small>
            </div>

            <div class="mb-3">
              <label for="midpointSalary" class="form-label">Midpoint Salary</label>
              <div class="input-group">
                <span class="input-group-text">KES</span>
                <input
                    type="number"
                    class="form-control"
                    id="midpointSalary"
                    v-model="gradeForm.midpoint_salary"
                    min="0"
                    step="0.01"
                >
                <button class="btn btn-outline-secondary" type="button" @click="calculateMidpoint">
                  Calculate
                </button>
              </div>
              <small class="text-muted">If not specified, midpoint will be calculated as average of min and max</small>
            </div>

            <div class="mb-3">
              <label for="structureId" class="form-label">Salary Structure</label>
              <select class="form-select" id="structureId" v-model="gradeForm.salary_structure_id">
                <option value="">No Structure</option>
                <option v-for="structure in salaryStructures" :key="structure.id" :value="structure.id">
                  {{ structure.structure_name }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label for="description" class="form-label">Description</label>
              <textarea
                  class="form-control"
                  id="description"
                  v-model="gradeForm.description"
                  rows="3"
              ></textarea>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button
              type="button"
              class="btn btn-primary"
              @click="savePayGrade"
              :disabled="!isFormValid || submitLoading"
          >
            <span v-if="submitLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {{ isEditing ? 'Update' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";

export default {
  name: 'PayGradesManagement',
  data() {
    return {
      loading: false,
      submitLoading: false,
      payGrades: [],
      salaryStructures: [],
      isEditing: false,
      salaryError: '',
      currentId: null,
      gradeForm: {
        grade_name: '',
        min_salary: '',
        max_salary: '',
        midpoint_salary: '',
        salary_structure_id: '',
        description: ''
      },
      modal: null,
      formModal: null
    };
  },
  computed: {
    isFormValid() {
      return this.gradeForm.grade_name &&
          this.gradeForm.min_salary &&
          this.gradeForm.max_salary &&
          parseFloat(this.gradeForm.min_salary) >= 0 &&
          parseFloat(this.gradeForm.max_salary) > 0 &&
          parseFloat(this.gradeForm.max_salary) > parseFloat(this.gradeForm.min_salary) &&
          !this.salaryError;
    }
  },
  mounted() {
    this.initModals();
  },
  methods: {
    initModals() {
      this.$nextTick(() => {
        // Initialize the main modal
        this.modal = new bootstrap.Modal(document.getElementById('payGradesManagementModal'));

        // Initialize form modal
        this.formModal = new bootstrap.Modal(document.getElementById('payGradeFormModal'));

        // Listen for modal hidden events to reset form
        document.getElementById('payGradeFormModal').addEventListener('hidden.bs.modal', this.resetForm);
      });
    },
    showModal() {
      if (this.modal) {
        this.loadPayGrades();
        this.loadSalaryStructures();
        this.modal.show();
      } else {
        this.initModals();
        setTimeout(() => {
          if (this.modal) {
            this.loadPayGrades();
            this.loadSalaryStructures();
            this.modal.show();
          }
        }, 100);
      }
    },
    hideModal() {
      if (this.modal) {
        this.modal.hide();
      }
    },
    showAddModal() {
      this.isEditing = false;
      this.currentId = null;
      this.resetForm();
      if (this.formModal) {
        this.formModal.show();
      }
    },
    showEditModal() {
      if (this.formModal) {
        this.formModal.show();
      }
    },
    hideFormModal() {
      if (this.formModal) {
        this.formModal.hide();
      }
    },
    resetForm() {
      this.gradeForm = {
        grade_name: '',
        min_salary: '',
        max_salary: '',
        midpoint_salary: '',
        salary_structure_id: '',
        description: ''
      };
      this.salaryError = '';
      this.submitLoading = false;
    },
    loadPayGrades() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getPayGrades");

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.payGrades = res.data.data.payGrades || [];
            } else {
              console.error("Error fetching pay grades:", res.data.message);
              alert(res.data.message || "Failed to load pay grades");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            alert("An error occurred while loading pay grades");
          });
    },
    loadSalaryStructures() {
      const data = new FormData();
      data.append("function", "getSalaryStructures");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.salaryStructures = res.data.data.salaryStructures || [];
            } else {
              console.error("Error fetching salary structures:", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
          });
    },
    editPayGrade(grade) {
      this.isEditing = true;
      this.currentId = grade.id;

      // Copy grade data to form
      this.gradeForm = {
        grade_name: grade.grade_name,
        min_salary: grade.min_salary,
        max_salary: grade.max_salary,
        midpoint_salary: grade.midpoint_salary || '',
        salary_structure_id: grade.salary_structure_id || '',
        description: grade.description || ''
      };

      this.showEditModal();
    },
    deletePayGrade(id) {
      if (!confirm("Are you sure you want to delete this pay grade? This may affect employee salaries.")) {
        return;
      }

      this.loading = true;

      const data = new FormData();
      data.append("function", "deletePayGrade");
      data.append("id", id);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              alert(res.data.message || "Pay grade deleted successfully");
              this.loadPayGrades();
            } else {
              alert(res.data.message || "Failed to delete pay grade");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            alert("An error occurred while deleting pay grade");
          });
    },
    calculateMidpoint() {
      if (this.gradeForm.min_salary && this.gradeForm.max_salary) {
        const min = parseFloat(this.gradeForm.min_salary);
        const max = parseFloat(this.gradeForm.max_salary);

        if (min >= 0 && max > 0 && max > min) {
          this.gradeForm.midpoint_salary = ((min + max) / 2).toFixed(2);
          this.salaryError = '';
        } else {
          this.salaryError = 'Maximum salary must be greater than minimum salary';
        }
      } else {
        alert("Please enter both minimum and maximum salary values first");
      }
    },
    validateSalaryRange() {
      if (this.gradeForm.min_salary && this.gradeForm.max_salary) {
        const min = parseFloat(this.gradeForm.min_salary);
        const max = parseFloat(this.gradeForm.max_salary);

        if (min >= max) {
          this.salaryError = 'Maximum salary must be greater than minimum salary';
          return false;
        } else {
          this.salaryError = '';
          return true;
        }
      }
      return true;
    },
    savePayGrade() {
      if (!this.isFormValid) {
        if (!this.validateSalaryRange()) {
          return;
        }
        alert("Please fill all required fields");
        return;
      }

      this.submitLoading = true;

      const data = new FormData();
      data.append("function", this.isEditing ? "updatePayGrade" : "createPayGrade");

      if (this.isEditing) {
        data.append("id", this.currentId);
      }

      data.append("grade_name", this.gradeForm.grade_name);
      data.append("min_salary", this.gradeForm.min_salary);
      data.append("max_salary", this.gradeForm.max_salary);

      if (this.gradeForm.midpoint_salary) {
        data.append("midpoint_salary", this.gradeForm.midpoint_salary);
      } else {
        // Calculate midpoint if not specified
        const min = parseFloat(this.gradeForm.min_salary);
        const max = parseFloat(this.gradeForm.max_salary);
        const midpoint = ((min + max) / 2).toFixed(2);
        data.append("midpoint_salary", midpoint);
      }

      if (this.gradeForm.salary_structure_id) {
        data.append("salary_structure_id", this.gradeForm.salary_structure_id);
      }

      if (this.gradeForm.description) {
        data.append("description", this.gradeForm.description);
      }

      execute(data, authUrl)
          .then((res) => {
            this.submitLoading = false;

            if (res.data.success) {
              alert(res.data.message || (this.isEditing ? "Pay grade updated successfully" : "Pay grade created successfully"));
              this.hideFormModal();
              this.loadPayGrades();
            } else {
              alert(res.data.message || "Failed to save pay grade");
            }
          })
          .catch((error) => {
            this.submitLoading = false;
            console.error("API Error:", error);
            alert("An error occurred while saving pay grade");
          });
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value || 0);
    }
  }
};
</script>

<style scoped>
.form-check {
  margin-bottom: 0.5rem;
}

.alert {
  margin-top: 1rem;
}
</style>